<script setup lang="ts">
import { computed } from 'vue';
import StfIcon from '../icon/StfIcon.vue';

const props = withDefaults(defineProps<{
    label?: string;
    modelValue?: boolean;
    disabled?: boolean;
    id?: string;
    size?: 'small' | 'medium';
}>(), {
    modelValue: false,
    disabled: false,
    id: '',
    label: '',
    size: 'medium'
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
}>();

const uniqueId = computed(() => {
    return props.id || `checkbox-${Math.random().toString(36).substring(2, 9)}`;
});

const isChecked = computed({
    get: () => props.modelValue,
    set: (value) => {
        if (!props.disabled) {
            emit('update:modelValue', value);
        }
    }
});

const toggleCheckbox = () => {
    if (!props.disabled) {
        isChecked.value = !isChecked.value;
    }
};

const getIconName = computed(() => {
    return isChecked.value ? 'squareCheck' : 'square';
});

const getIconColor = computed(() => {
    return isChecked.value && !props.disabled ? 'blue' : 'black';
});
</script>

<template>
    <div
        class="stf-checkbox"
        :class="{
            'stf-checkbox--disabled': disabled,
            'stf-checkbox--small': size === 'small'
        }"
    >
        <div class="stf-checkbox__container" @click="toggleCheckbox">
            <StfIcon
                :icon="getIconName"
                :size="size"
                :color="getIconColor"
            />
            <label v-if="label.length > 0" :for="uniqueId" class="stf-checkbox__label">
                {{ label }}
            </label>
        </div>
        <input
            type="checkbox"
            :id="uniqueId"
            v-model="isChecked"
            :disabled="disabled"
            class="stf-checkbox__input"
        />
    </div>
</template>

<style lang="scss" scoped>
@use '@/assets/sass/_variables' as vars;

.stf-checkbox {
  //display: inline-flex; Might need to enable this again
  align-items: center;

  &__container {
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 8px;
  }

  &__label {
    font-family: vars.$font-family-primary;
    font-size: vars.$font-size-14;
    font-weight: vars.$font-weight-normal;
    line-height: 1.4;
    cursor: pointer;
  }

  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;

    .stf-checkbox__container,
    .stf-checkbox__label {
      cursor: not-allowed;
    }
  }

  &--small {
    .stf-checkbox__label {
      font-size: vars.$font-size-12;
    }
  }
}
</style>
